import {ConfirmationDialog, Link} from '@primer/react'

import styles from './RebaseStackConfirmationDialog.module.css'

/**
 * The in-app confirmation shown before a stack rebase runs. Triggers the rebase on confirm.
 *
 * Presentational: the parent owns the trigger button, the open state, and the actual rebase
 * mutation.
 */
export function RebaseStackConfirmationDialog({
  onConfirm,
  onClose,
  signedRebaseEnabled = false,
}: {
  onConfirm: () => void
  onClose: () => void
  signedRebaseEnabled: boolean
}) {
  const commitAttributionCopy = signedRebaseEnabled ? (
    <>
      Commits keep their original author, but will be signed and verified by GitHub.{' '}
      <Link
        inline
        href="https://docs.github.com/en/free-pro-team@latest/authentication/managing-commit-signature-verification"
        target="_blank"
      >
        Learn more about commit signature verification.
      </Link>
    </>
  ) : (
    'Commits keep their original author, but you will be listed as the committer.'
  )

  return (
    <ConfirmationDialog
      width="xlarge"
      title="Rebase stack"
      onClose={gesture => {
        if (gesture === 'confirm') {
          onConfirm()
        }
        onClose()
      }}
      confirmButtonContent="Rebase stack"
      confirmButtonType="primary"
    >
      <div className={styles.content}>
        <p>
          Rebasing a stack restores a linear commit history when its branches have diverged, and brings the stack up to
          date with its base branch.{' '}
          <Link inline href="https://gh.io/stacks-rebasing-your-stack">
            Learn more about rebasing stacks.
          </Link>
        </p>
        <div className={styles.steps}>
          <h2 id="rebase-steps-heading" className={styles.stepsTitle}>
            What will happen
          </h2>
          {/* role="list" restores list semantics that Safari/VoiceOver drops when list-style: none */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ol aria-labelledby="rebase-steps-heading" className={styles.stepsList} role="list">
            <li className={styles.step}>
              Each branch is rebased onto the tip of the one below it, starting from the first unmerged branch.
            </li>
            <li className={styles.step}>Once a branch is rebased, it is force-pushed.</li>
            <li className={styles.step}>The process stops at the first merge conflict or failed rebase.</li>
          </ol>
        </div>
        <p>{commitAttributionCopy}</p>
      </div>
    </ConfirmationDialog>
  )
}
